/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react'
import { faArrowRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLocation, useNavigate } from 'react-router-dom'
import { animateScroll } from 'react-scroll'
import { useDispatch, useSelector } from 'react-redux'
import constants, { capitalise } from '../../utilities/constants'
import { setIsRoleFlow, setRoleName, setTargetRole, setUTMDetails } from '../../redux/skillSlice'
import { toast } from 'react-toastify'
import useWindowSize from '../../hooks/useWindowSize'
import { setLastVisitedUrl } from '../../redux/nameSlice'
import useDebounce from '../../hooks/useDebounce'
import RoleSearchSkeleton from '../../components/RoleSearchSkeleton'
import { Mixpanel } from '../../config/Mixpanel'
import 'swiper/css'
import { ArrowUpRight } from 'lucide-react'
import { fetchUserInitSession } from '../../redux/onboardingUserDetails'
import ProgressBar from '../../components/ProgressBar'
import { selectIsAnalysisPageVisited, selectIsPricingPageVisited } from '../../redux/newAnalysisSlice'

const RoleSearch = () => {

  // Popular roles data
  const popularRoles = [
    { role_name: 'Data Analyst', total_count: 3708 },
    { role_name: 'HR Manager', total_count: 2432 },
    { role_name: 'Project Manager', total_count: 1794 },
    { role_name: 'SQL Developer', total_count: 815 },
    { role_name: 'Cybersecurity Analyst', total_count: 808 },
    { role_name: 'Team Lead', total_count: 721 },
    { role_name: 'Business Analyst', total_count: 672 },
    { role_name: 'Operations Manager', total_count: 664 },
    { role_name: 'Quality Manager', total_count: 589 },
    { role_name: 'AI Engineer', total_count: 563 },
    { role_name: 'Prompt Engineer', total_count: 344 },
    { role_name: 'BI Analyst', total_count: 325 },
    { role_name: 'People Manager', total_count: 288 },
    { role_name: 'AI Agent Developer', total_count: 284 },
    { role_name: 'Automation Test Engineer', total_count: 274 },
    { role_name: 'Data Scientist', total_count: 274 },
    { role_name: 'AI Automation Engineer', total_count: 260 }
  ]
  // Function to format numbers with K suffix
  const formatRoleCount = (count) => {
    if (!count) return '0'
    if (count < 1000) return count.toString()

    const thousands = count / 1000
    // If it's a whole number of thousands, show without decimal
    if (thousands >= 10) return Math.floor(thousands) + 'K'
    // For numbers like 1400 -> 1.4K, 2300 -> 2.3K
    return (Math.round(thousands * 10) / 10) + 'K'
  }

  const location = useLocation()

  const params = new URLSearchParams(location.search)
  const utm_source = params.get('utm_source')
  const utm_campaign = params.get('utm_campaign')
  const utm_medium = params.get('utm_medium')

  const inputRef = useRef(null)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { height, width } = useWindowSize()

  const [goalValue, setGoalValue] = useState('')
  const [displayedRoles, setDisplayedRoles] = useState([])
  const [selectedRole, setSelectedRole] = useState([])
  const [searchResult, setSearchResult] = useState([])
  const [slicingIndex, setSlicingIndex] = useState(width <= 768 ? height >= 844 ? 10 : 5 : 15);
  const [scrollToTop, setScrollToTop] = useState(false)
  const [showTitle, setShowTitle] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const { debouncedValue } = useDebounce(goalValue)

  const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited)
  const isPricingPageVisited = useSelector(selectIsPricingPageVisited)


  useEffect(() => {
    if (isAnalysisPageVisited) {
      navigate('/onboarding/analysis')
    }

    else if (isPricingPageVisited) {
      navigate('/onboarding/pricing')
    }

  }, [dispatch, isAnalysisPageVisited, isPricingPageVisited, navigate])


  // Search roles function
  const searchRoles = async (query) => {
    try {
      setIsLoading(true)
      const response = await fetch(`https://assess-backend.learntube.ai/search_jobs?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error searching roles:', error)
      return { success: false, results: [] }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    localStorage.setItem('showScholarship', JSON.stringify({}))
    localStorage.setItem('visitedAnalysisPage', JSON.stringify(false))

    dispatch(setUTMDetails({ utm_source: utm_source ? utm_source : "", utm_medium: utm_medium ? utm_medium : "", utm_campaign: utm_campaign ? utm_campaign : "" }))
    dispatch(setLastVisitedUrl('/roles/search'))
    dispatch(setIsRoleFlow(true))

    // Track page load
    Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_PAGE_LOADED, {
      utm_source: utm_source || '',
      utm_medium: utm_medium || '',
      utm_campaign: utm_campaign || '',
      timestamp: new Date().toISOString()
    })

    if (inputRef.current) {
      inputRef.current.focus()
    }
    setDisplayedRoles(popularRoles.slice(0, 20))

  }, [])

  useEffect(() => {
    if (!showTitle && goalValue.length > 0 && !scrollToTop) {
      setScrollToTop(true)
    }
  }, [goalValue, showTitle])

  useEffect(() => {
    if (scrollToTop) {
      animateScroll.scrollMore(height <= 740 ? 80 : 250, { duration: 300 })
    } else {
      animateScroll.scrollToTop({ duration: 300 })
    }
  }, [scrollToTop])

  useEffect(() => {
    if (debouncedValue) {
      const fetchData = async () => {
        try {
          const res = await searchRoles(debouncedValue)

          // Handle the actual API response format
          if (res?.success && res?.results?.length > 0) {
            setSearchResult(res.results)
          } else {
            setSearchResult([debouncedValue])
          }
        } catch (error) {
          console.log('Search error:', error)
          setSearchResult([debouncedValue])
        }
      }
      fetchData()
    } else {
      setSearchResult([])
    }
  }, [debouncedValue])

  const handleSubmit = async (e) => {
    e.stopPropagation()
    e.preventDefault()

    if (goalValue === '') {
      toast.error('Type a role you want to pursue')
      return
    }

    const roleName = capitalise(goalValue)
    dispatch(setRoleName(roleName))
    dispatch(setTargetRole(roleName))

    try {
      await dispatch(fetchUserInitSession({ anon_id: "", skill_name: "", role: roleName })).unwrap()
    } catch (error) {
      toast.error('Failed to start your session. Please try again.')
      return
    }

    // Track form submission
    Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_FORM_SUBMITTED, {
      role_name: roleName,
      search_method: searchResult.length > 0 && searchResult.includes(goalValue) ? 'search_result' : 'manual_input',
      timestamp: new Date().toISOString()
    })

    navigate(`/roles/skills`)
  }

  const handleRoleSelection = (index) => {
    setShowTitle(true)
    setScrollToTop(false)
    const tempArray = [...Array(selectedRole.length).fill(false)]
    tempArray[index] = true
    setSelectedRole(tempArray)
    setGoalValue(displayedRoles[index].role_name)

    // Track popular role selection
    Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_POPULAR_ROLE_CLICKED, {
      role_name: displayedRoles[index].role_name,
      role_count: displayedRoles[index].total_count,
      role_index: index,
      timestamp: new Date().toISOString()
    })
  }

  const handleDropResults = (index) => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
    setShowTitle(false)
    setGoalValue(searchResult[index])

    // Track dropdown result selection
    Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_DROPDOWN_RESULT_CLICKED, {
      selected_role: searchResult[index],
      search_query: debouncedValue,
      result_index: index,
      total_results: searchResult.length,
      timestamp: new Date().toISOString()
    })
  }

  return (
    <>
      <div className='mt-8 w-full md:max-w-[45rem] flex flex-col justify-between items-center flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative gap-6' onClick={(e) => {
        setShowTitle(true)
        setScrollToTop(false)
      }}>
        <div className='flex flex-col justify-start items-center w-full'>
          <ProgressBar
            currentStep={1}
            totalSteps={8}
            stepText={
              `Step 1/8 of becoming _______ `
            }
          />


          <div className='w-full mt-4 flex flex-col justify-start items-center p-[2px] rounded-[1.5rem]' style={{
            backgroundImage: `linear-gradient(
            to right,
            rgba(64, 106, 255, 0.3) 0%,
            rgba(64, 106, 255, 1) 56%,
            rgba(64, 106, 255, 0.3) 100%
          )`
          }}>
            {/* Main content card */}
            <div className='flex flex-col p-6 rounded-[1.5rem]  shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] w-full relative' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>
              <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px] pointer-events-none"
                style={{ backgroundImage: `url('https://img.learntube.ai/public-assets/personalise/web.webp')` }} />
              <div className={`flex md:flex-1 flex-col justify-center`}>
                <div className='flex flex-col'>
                  <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                    Which role are you aiming for?
                  </h1>
                </div>
                <form className='mt-6' onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
                  <div className='relative mt-2'>
                    <span className='pointer-events-none absolute inset-y-0 left-3 flex items-center text-white/60'>
                      <FontAwesomeIcon icon={faMagnifyingGlass} className='text-sm' />
                    </span>
                    <input
                      type="text"
                      ref={inputRef}
                      className='bg-[#406AFF]/10 text-[0.875rem] text-white rounded-[0.75rem] h-[3.5625rem] pl-9 pr-28 w-full mx-auto user_goal box-glow placeholder-white/30 placeholder:text-sm'
                      placeholder='For e.g. Data Scientist, HR Manager, etc.'
                      value={goalValue}
                      onFocus={(e) => {
                        e.stopPropagation()
                        setShowTitle(false)
                        // Track input focus
                        Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_INPUT_FOCUSED, {
                          timestamp: new Date().toISOString()
                        })
                      }}
                      onChange={(e) => {
                        e.stopPropagation()
                        if (showTitle) {
                          setShowTitle(false)
                        }
                        setGoalValue(e.target.value)

                        // Track search query typing with debounce to avoid too many events
                        if (e.target.value.length > 0) {
                          Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_QUERY_TYPED, {
                            query: e.target.value,
                            query_length: e.target.value.length,
                            timestamp: new Date().toISOString()
                          })
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {(
                      <button
                        type="submit"
                        disabled={!goalValue}
                        className='absolute inset-y-3 right-3 bg-[#7FC241] h-[2.0625rem] disabled:opacity-50 text-black font-semibold rounded-lg px-4 text-sm flex items-center justify-center gap-2 cursor-pointer'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FontAwesomeIcon icon={faArrowRight} className='text-sm' />
                      </button>
                    )}
                  </div>
                </form>

                {(!showTitle && goalValue) && <div
                  className='mt-4 rounded-[0.75rem]'
                  style={{ boxShadow: '0 0 0.4375rem 0 #7FC241' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {(searchResult.length <= 0 || isLoading) ?
                    <div className='h-[7.125rem] w-full rounded-[0.75rem]'>
                      {/* <RoleSearchSkeleton /> */}
                    </div> :
                    <div
                      className={`rounded-[0.75rem] border border-[#7FC241] h-[7.125rem] pl-2 pr-4 py-2 overflow-y-auto`}
                      style={{ backgroundColor: `linear-gradient(to right, rgba(7, 25, 51, 0.8) 0%, rgba(8, 47, 89, 0.8) 55%, rgba(7, 25, 51, 0.8) 100%)` }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {searchResult?.map((item, index) => (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            handleDropResults(index)
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation()
                          }}
                          className={`text-left ${goalValue === item ? '' : ''} text-[#70AEFF] font-medium text-[0.75rem] px-3 py-2.5 border-b border-white/10 flex justify-between items-center cursor-pointer hover:bg-white/5 active:bg-white/10 transition-colors select-none`}
                          style={{
                            WebkitTapHighlightColor: 'transparent',
                            WebkitTouchCallout: 'none',
                            WebkitUserSelect: 'none',
                            userSelect: 'none'
                          }}
                        >
                          {item}
                          <ArrowUpRight className='w-4 h-4 font-bold text-[#70AEFF]/35 pointer-events-none' strokeWidth={2.5} />
                        </div>
                      ))}
                    </div>
                  }
                </div>}
                {(showTitle || !goalValue) && <>
                  <div className={`flex justify-between items-center ${height <= 740 ? 'mt-10' : 'mt-10'} mb-4 text-[#90BEED]`}>
                    <p className='text-[12px] font-medium text-white/50'>MOST LEARNERS CHOOSE THESE ROLES</p>
                  </div>
                  <div className={`${displayedRoles?.length < 3 ? '' : 'takeaway'}`}>
                    <div className='flex gap-2 flex-wrap items-center'>
                      {displayedRoles?.slice(0, slicingIndex)?.map((item, index) => {

                        return (
                          (
                            <div key={index} className={`${goalValue === item.role_name ? 'bg-[#406AFF]' : 'bg-[#406AFF]/15 text-[#9ABFE5] text-[#70AEFF]'}  border border-[#406AFF]/50 h-[46px] flex items-center justify-center px-3 cursor-pointer rounded-[0.5rem] text-[0.75rem] font-medium relative select-none`} onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              handleRoleSelection(index)
                            }}
                              onTouchStart={(e) => {
                                e.stopPropagation()
                              }}
                              style={{
                                WebkitTapHighlightColor: 'transparent',
                                WebkitTouchCallout: 'none',
                                WebkitUserSelect: 'none',
                                userSelect: 'none'
                              }}
                            >
                              {item.role_name}
                              <div className={`absolute top-0 right-0 flex justify-center items-center gap-1 rounded-tr-[0.5rem] rounded-bl-[0.5rem] ${goalValue === item.role_name ? 'bg-white/20' : 'bg-[#406AFF]/15'} text-white/70 text-[9px] font-medium leading-none px-1 py-[3px] pointer-events-none`}>
                                <img src="https://img.learntube.ai/public-assets/personalise/Vector%20(15).webp" alt="" width={7} height={7} />{formatRoleCount(item.total_count)}</div>
                            </div>
                          )
                        )
                      })}
                      {
                        slicingIndex < displayedRoles?.length ?
                          <div
                            className='cursor-pointer text-[0.75rem] font-semibold text-[#70AEFF] underline underline-offset-2 select-none'
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              setSlicingIndex(displayedRoles?.length)
                              // Track show more clicked
                              Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_SHOW_MORE_CLICKED, {
                                current_displayed: slicingIndex,
                                total_roles: displayedRoles?.length,
                                additional_roles_shown: displayedRoles?.length - slicingIndex,
                                timestamp: new Date().toISOString()
                              })
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation()
                            }}
                            style={{
                              WebkitTapHighlightColor: 'transparent',
                              WebkitTouchCallout: 'none',
                              WebkitUserSelect: 'none',
                              userSelect: 'none'
                            }}
                          >
                            +{displayedRoles?.length - slicingIndex} more
                          </div> :
                          <div
                            className='cursor-pointer text-[0.75rem] font-semibold text-[#70AEFF] underline underline-offset-2 select-none'
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              setSlicingIndex(width <= 768 ? 5 : 10)
                              // Track view less clicked
                              Mixpanel.track(constants.Mixpanel.ROLE_SEARCH_VIEW_LESS_CLICKED, {
                                current_displayed: slicingIndex,
                                reduced_to: width <= 768 ? 5 : 10,
                                timestamp: new Date().toISOString()
                              })
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation()
                            }}
                            style={{
                              WebkitTapHighlightColor: 'transparent',
                              WebkitTouchCallout: 'none',
                              WebkitUserSelect: 'none',
                              userSelect: 'none'
                            }}
                          >
                            View Less
                          </div>
                      }
                    </div>
                  </div>
                </>}
              </div>

            </div>
          </div>
        </div>


        <div className='flex flex-col gap-3 items-center justify-center w-full mt-6'>
          {/* Company logos */}
          <div className='flex items-center pr-2'>
            <div className='w-[66px] h-[28px]'>
              <img alt="Company logo" className='w-full h-full object-cover' src="https://img.learntube.ai/public-assets/personalise/Frame%20427321976%20(2).webp" />
            </div>
          </div>

          {/* Certification text */}
          <div className='text-center'>
            <p className='font-medium italic text-[0.75rem] text-[#83BEFF] leading-[1.4]' style={{
              background: "linear-gradient(to right, #FFFFFF 0%, #1156A3 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
            >
              +1,100 companies recognise our certificate for roles they’re actively hiring
            </p>
          </div>
        </div>

      </div>
    </>
  )
}

export default RoleSearch
